import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { api } from '@/context/AuthContext';
import { Crown, Lock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';const SubscriptionContext = createContext({
  isPremium: false,
  features: {},
  limits: null,
  loading: true,
  refresh: () => {},
});

export function SubscriptionProvider({ children }) {
  const [data, setData] = useState({ isPremium: false, features: {}, limits: null, loading: true });

  const refresh = useCallback(async () => {
    // Always attempt the call — the httpOnly cookie is invisible to JS, so we
    // can't pre-check it. /subscription/features returns is_premium=false for
    // unauthenticated requests anyway, so this is safe.
    try {
      const res = await api.get('/subscription/features');
      setData({
        isPremium: res.data.is_premium,
        features: res.data.features || {},
        limits: res.data.limits,
        loading: false,
      });
    } catch {
      setData({ isPremium: false, features: {}, limits: null, loading: false });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-check when auth changes or subscription updates (e.g. after checkout)
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('auth-changed', handler);
    window.addEventListener('sahiri:subscription-changed', handler);
    return () => {
      window.removeEventListener('auth-changed', handler);
      window.removeEventListener('sahiri:subscription-changed', handler);
    };
  }, [refresh]);

  return (
    <SubscriptionContext.Provider value={{ ...data, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}

export function PremiumBadge({ small }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${small ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'}`}
      style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-primary)' }}>
      <Crown className={small ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      Members
    </span>
  );
}

export function PremiumGate({ feature, children, fallback, message }) {
  const { isPremium, features } = useSubscription();
  const navigate = useNavigate();

  if (isPremium || features[feature]) {
    return children;
  }

  if (fallback) return fallback;

  return (
    <div className="relative">
      <div className="opacity-40 pointer-events-none select-none blur-[2px]">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className="p-6 rounded-2xl text-center max-w-sm mx-4"
          style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
          <Lock className="w-6 h-6 mx-auto mb-3" style={{ color: 'var(--accent)' }} />
          <h3 className="font-heading text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
            Unlock with a Sahiri plan
          </h3>
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
            {message || 'Unlock this feature with an Individual, Couple, or Family plan to go deeper in your journey.'}
          </p>
          <button
            data-testid="premium-gate-upgrade-btn"
            onClick={() => navigate('/premium')}
            className="flex items-center gap-2 mx-auto px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-[1.03]"
            style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-primary)' }}
          >
            <Crown className="w-4 h-4" />
            Upgrade
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function PremiumInline({ feature, message, children }) {
  const { isPremium, features } = useSubscription();
  const navigate = useNavigate();

  if (isPremium || features[feature]) return children;

  return (
    <button
      onClick={() => navigate('/premium')}
      className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm transition-all hover:scale-[1.02] w-full"
      style={{ backgroundColor: 'rgba(192,130,97,0.08)', border: '1px solid rgba(192,130,97,0.2)', color: 'var(--accent)' }}
      data-testid="premium-inline-upgrade"
    >
      <Crown className="w-4 h-4 flex-shrink-0" />
      <span className="text-left flex-1">{message || 'Upgrade your plan'}</span>
      <ArrowRight className="w-3 h-3" />
    </button>
  );
}
